import {
	CONFLUENCE_OPERATIONAL_EVENT_TYPE,
	Product,
} from '../../common/constants/event-attributes';
import { createEventPayload } from '../../utils/create-event-payload';
import {
	convertEventAttributesForJiraFireEvent,
	convertEventPayloadForJiraCreateAnalyticsEvent,
} from '../../utils/jira-formatter';
import { validateExperimentName } from '../../utils/validate-experiment-name';

import type { AnalyticsEventPayloadForProduct, ErrorEventParamsForProduct } from './types';

/**
 * Formats an error event with standardized metadata for analytics tracking.
 *
 * @param metadata Error event metadata including product, team name, experiment name, severity, error type, error message, and additional attributes
 * @param isActiveExperiment Whether the experiment is currently active (defaults to true) - this is used to determine if the experiment is active and should be tracked. Change this to false if the experiment is productionizing.
 * @returns A formatted analytics event payload that can be sent to the analytics system
 */
export const createProductSpecificAnalyticsIntegrationPayload = (
	{
		product,
		team,
		experimentMeta,
		errorType,
		errorMessage,
		source,
		severity,
		additionalAttributes = {},
	}: ErrorEventParamsForProduct,
	isActiveExperiment = true,
): AnalyticsEventPayloadForProduct => {
	validateExperimentName(experimentMeta.experimentKey);
	const eventPayload = createEventPayload({
		team,
		experimentMeta,
		errorType,
		errorMessage,
		source,
		severity,
		isActiveExperiment,
		additionalAttributes,
		product,
	});

	switch (product) {
		case Product.ADMIN_HUB:
			return {
				data: eventPayload,
			};
		case Product.JIRA: {
			return {
				data: eventPayload,
				productSpecificRules: {
					jira: {
						createAnalyticsEventPayload: convertEventPayloadForJiraCreateAnalyticsEvent({
							data: eventPayload,
						}),
						fireEventPayload: convertEventAttributesForJiraFireEvent({ data: eventPayload }),
					},
				},
			};
		}
		case Product.CONFLUENCE:
			return {
				type: CONFLUENCE_OPERATIONAL_EVENT_TYPE,
				data: eventPayload,
			};
		default:
			throw new Error(`Unsupported product: ${product}`);
	}
};
